import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {apiClient} from 'common/util/api-client';
import {initMixpanel} from 'common/report/tracking-setup';
import {reporter} from 'common/report/error-reporting';
import {promisifyModal} from 'common/components/modal/utils';
import {getProjectId} from 'common/report/util';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {Section, SectionItem} from 'common/components/items-menu/types';

import {FeedbackForFeature, Sentiment} from './types';
import {FeedbackType} from './types';
import {template} from './template';
import css from './index.inline.styl';

export type FeedbackModalAttrs = {
  feature: FeedbackForFeature;
  'is-staff-user'?: boolean;
  'submit-button-text'?: string;
  title?: string;
  placeholder?: string;
};

export type FeedbackModalParams = {
  extraFeedbackEventName?: string;
  extraFeedbackEventProperties?: Record<string, any>;
  initialSentiment?: Sentiment;
};

export type FeedbackModalState = {
  feedback: string;
  customerEmail?: string;
  feedbackType?: FeedbackType;
  sentiment?: Sentiment;
  saving: boolean;
  submitErrorMsg: string;
};

type FeedbackModalJsxProps = {
  attrs: FeedbackModalAttrs;
  on: {
    close: (ev: CustomEvent) => void;
  };
  params: FeedbackModalParams;
};

export class FeedbackModal extends Component<
  FeedbackModalState,
  FeedbackModalAttrs,
  unknown,
  unknown,
  unknown,
  FeedbackModalParams
> {
  mixpanel = null;

  static get attrsSchema(): AttrsSchema<FeedbackModalAttrs> {
    return {
      feature: {
        type: `string`,
        description: `An identifier for the feature the user is providing feedback on. E.g. "live view 2021"`,
      },
      'is-staff-user': {type: `boolean`, description: `Is the current user a staff (@mixpanel.com) user`},
      'submit-button-text': {type: `string`, default: `Submit`},
      title: {type: `string`, default: `Submit Feedback`},
      placeholder: {
        type: `string`,
        description: `The placeholder text shown in the textarea`,
        default: `Have feedback? We'd love to hear about your experience. If you have questions, visit our Help Center or contact support.`,
      },
    };
  }

  constructor() {
    super();
    this.mixpanel = initMixpanel(PROJECT_3_MIXPANEL_TOKEN);
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        feedback: ``,
        customerEmail: null,
        feedbackType: null,
        saving: false,
        submitErrorMsg: null,
      },
      params: {
        extraFeedbackEventName: String,
        extraFeedbackEventProperties: Object,
        initialSentiment: String,
      },
    };
  }

  handleFeedbackInput = (ev: InputEvent) => {
    ev.stopPropagation();
    this.update({feedback: (ev.target as HTMLFormElement).value});
  };

  handleCustomerEmailInput = (ev: InputEvent) => {
    ev.stopPropagation();
    this.update({customerEmail: (ev.target as HTMLFormElement).value});
  };

  getFeedbackTypeSections(): Section[] {
    return [
      {
        items: [
          {value: null, label: `Unspecified`},
          {value: FeedbackType.Dealbreaker, label: `Dealbreaker`},
          {value: FeedbackType.PainPoint, label: `Pain-point`},
          {value: FeedbackType.Beta, label: `Alpha/beta`},
          // Removed NotWorking option at request of product but leaving commented out
          // since the db still has this value for historical purposes and I want to make it clear it's intentional
          // {value: FeedbackType.NotWorking, label: `Not working as expected (e.g. bugs)`},
        ].map((option) => {
          return {...option, isSelected: option.value === this.state.feedbackType};
        }),
      },
    ];
  }

  handleSelectFeedbackType = (item: SectionItem) => {
    this.update({feedbackType: item.value});
  };

  handleModalChange = (ev: CustomEvent) => {
    ev.stopPropagation();
    if (ev.detail && ev.detail.state === `closed`) {
      this.closeModal();
    }
  };

  closeModal = (submitted = false) => {
    this.dispatchEvent(new CustomEvent(`close`, {detail: {submitted, sentiment: this.state.sentiment}}));
  };

  submitFeedback = async () => {
    try {
      this.update({saving: true, submitErrorMsg: null});

      await apiClient.post(`/api/app/projects/${getProjectId()}/product-feedback`, {
        feature: this.attr(`feature`),
        feedback: this.state.feedback,
        sentiment: this.state.sentiment,
        feedback_type: this.state.feedbackType,
        customer_email: this.state.customerEmail,
        url: window.location.href,
      });
      const feedbackProps = {
        customer_email: this.state.customerEmail,
        feature: this.attr(`feature`),
        feedback: this.state.feedback,
        feedback_type: this.state.feedbackType,
        is_staff_user: this.attr(`is-staff-user`),
        sentiment: this.state.sentiment,
      };
      this.track(`Product Feedback Submitted`, feedbackProps);

      // Submit an extra event if one was passed.
      if (this.params.extraFeedbackEventName) {
        this.track(this.params.extraFeedbackEventName, {
          ...feedbackProps,
          ...this.params.extraFeedbackEventProperties,
        });
      }

      this.closeModal(true);
      // this brings up the success toast
      addToast({
        message: `Thank you for submitting feedback`,
        hide: false,
        type: ToastType.SUCCESS,
      });
    } catch (err) {
      this.update({submitErrorMsg: (err as Error).message});
    } finally {
      this.update({saving: false});
    }
  };

  track(eventName: string, properties = {}): void {
    try {
      this.mixpanel.track(eventName, properties);
    } catch (err) {
      reporter.error(`tracking error`, err);
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.track(`Product Feedback Modal Opened`, {
      feature: this.attr(`feature`),
    });
    if (this.params.initialSentiment) {
      this.update({sentiment: this.params.initialSentiment});
    }
  }
}

export async function promptFeedbackModal(
  attrs: FeedbackModalAttrs,
  params: FeedbackModalParams = {},
): Promise<[boolean, Sentiment]> {
  const closeEv = await promisifyModal({
    tagName: `mp-feedback-modal`,
    attrs,
    params,
    closeEvents: [`close`],
  });

  return [closeEv.detail.submitted, closeEv.detail.sentiment];
}

export const FeedbackModalEl = defineAndMakeJsxFactory<FeedbackModalJsxProps>(`mp-feedback-modal`, FeedbackModal);
export const FeedbackModalReact = panel2react<FeedbackModalJsxProps>(`mp-feedback-modal`, FeedbackModal);
